import { z } from 'zod';
import { Cents, PositionKey, Price, PriceForm, PriceUnit } from './catalog-prices.js';
import { ASK_EVERYTHING, DraftQuestions, draftQuestionsOf } from './draft-questions.js';
import { InquiryService } from './intake.js';
/**
 * The firm's catalogue of services and fees (ADR-071 §3): which areas, packages and positions of
 * the building-block library the firm offers, at which price, with which firm-standard values.
 * The library brings the structure (the standard catalogue, `catalog.yaml` of
 * `@kanos/contract-library`); the firm only switches on and off and sets prices - Kanos sets no
 * price of its own.
 */
/** The areas of services, as the library groups its blocks (`E1.FIBU.010` - area `FIBU`). */
export const CatalogArea = z
    .enum(['EINR', 'FIBU', 'LOHN', 'JA', 'BST', 'PST', 'BER', 'IND'])
    .meta({ id: 'CatalogArea' });
export const catalogAreaLabel = {
    EINR: 'Ersteinrichtung',
    FIBU: 'Finanzbuchhaltung',
    LOHN: 'Lohn- und Gehaltsabrechnung',
    JA: 'Jahresabschluss und EÜR',
    BST: 'Betriebliche Steuern',
    PST: 'Private Steuern',
    BER: 'Beratung',
    IND: 'Individuelle Leistung',
};
/**
 * The area each service of the inquiry form belongs to (ADR-071 §3): the keys of ADR-068 stay
 * valid, the catalogue's areas say which of them the firm offers. Founding and succession are
 * advice (`BER`) - the library has no area of their own.
 */
export const areaOfInquiryService = {
    bookkeeping: 'FIBU',
    payroll: 'LOHN',
    annual_accounts: 'JA',
    business_tax_returns: 'BST',
    private_tax_returns: 'PST',
    tax_advice: 'BER',
    business_formation: 'BER',
    succession: 'BER',
};
/** How the firm keeps the books of a client (E1.FIBU.200-290). */
export const BookkeepingModel = z
    .enum(['package', 'core', 'self_booking'])
    .meta({ id: 'BookkeepingModel' });
export const bookkeepingModelLabel = {
    package: 'Pakete',
    core: 'Kernleistung ohne Paket',
    self_booking: 'Unterstützung von Selbstbuchern',
};
export const BookkeepingPackage = z
    .enum(['BASIS', 'CLASSIC', 'PREMIUM'])
    .meta({ id: 'BookkeepingPackage' });
/** A block of the library by its ID (`E1.FIBU.010`), as `@kanos/contract-library` checks it. */
export const LibraryBlockId = z
    .string()
    .regex(/^E[0-7]\.[A-Z]+\.\d{3}$/, 'Block IDs look like E1.FIBU.010')
    .meta({ id: 'LibraryBlockId' });
/** What a position is to the client: the service itself, a package, an add-on, a surcharge … */
export const CatalogPositionKind = z
    .enum([
    'base',
    'package',
    'option',
    'additional_service',
    'on_demand_service',
    'surcharge',
    'relief',
    'discount',
    'minimum_fee',
    'extra_work',
    'expense',
])
    .meta({ id: 'CatalogPositionKind' });
/**
 * A position of the standard catalogue: the block it stands for and the price forms it takes,
 * each with the block of the fee agreement that prints it. An `option` has none - it is part of
 * the service it belongs to and switched on or off only.
 */
export const CatalogTemplatePosition = z
    .object({
    key: PositionKey,
    kind: CatalogPositionKind,
    block: LibraryBlockId,
    /** Its name where the block's title does not tell it apart (one block, several positions). */
    title: z.string().trim().min(1).max(120).optional(),
    prices: z.partialRecord(PriceForm, LibraryBlockId),
    /** The unit a price per unit or a tier counts by default. */
    unit: PriceUnit.optional(),
    /** The provision of the StBVV a value fee takes by default (`33-1`). */
    provision: z.string().min(1).max(40).optional(),
    /** Only with this model of bookkeeping (FIBU). */
    model: BookkeepingModel.optional(),
})
    .strict()
    .meta({ id: 'CatalogTemplatePosition' });
/** A bookkeeping package: its position and the blocks it includes (STBV-B1 3.1, the matrix). */
export const CatalogTemplatePackage = z
    .object({
    id: BookkeepingPackage,
    position: PositionKey,
    includes: z.array(LibraryBlockId).min(1),
})
    .strict()
    .meta({ id: 'CatalogTemplatePackage' });
export const CatalogTemplateArea = z
    .object({
    area: CatalogArea,
    positions: z.array(CatalogTemplatePosition),
    packages: z.array(CatalogTemplatePackage).default([]),
})
    .strict()
    .meta({ id: 'CatalogTemplateArea' });
/** The standard catalogue of the library: every area once, in order, and the general positions. */
export const CatalogTemplate = z
    .object({
    areas: z
        .array(CatalogTemplateArea)
        .refine((areas) => areas.map((a) => a.area).join() === CatalogArea.options.join(), 'Every area once, in the order of CatalogArea'),
    general: z.array(CatalogTemplatePosition),
})
    .strict()
    .meta({ id: 'CatalogTemplate' });
/** Every position of the template, the general ones last. */
export const templatePositions = (template) => [
    ...template.areas.flatMap((a) => a.positions),
    ...template.general,
];
const DayOfMonth = z.number().int().min(1).max(31).nullable();
const ListItem = z.string().trim().min(1).max(120);
/** When the documents for the annual accounts are due (E3.JA.070): a date, or relative. */
export const AnnualAccountsDeadline = z
    .discriminatedUnion('kind', [
    z.object({
        kind: z.literal('date_following_year'),
        day: z.number().int().min(1).max(31),
        month: z.number().int().min(1).max(12),
    }),
    z.object({ kind: z.literal('after_reference_date'), months: z.number().int().min(1).max(12) }),
    z.object({ kind: z.literal('before_filing_date'), months: z.number().int().min(1).max(12) }),
])
    .meta({ id: 'AnnualAccountsDeadline' });
export const NoticePeriod = z
    .object({ amount: z.number().int().min(1).max(12), unit: z.enum(['week', 'month']) })
    .meta({ id: 'NoticePeriod' });
/**
 * The firm-standard values the library leaves to the firm (`category: firm_standard`). `null`:
 * not set yet - the catalogue names it as missing; Kanos fills in no value of its own.
 */
export const FirmStandards = z
    .object({
    timeRounding: z.enum(['half_hour_daily', 'minute']).nullable(),
    mediumFeeMinimum: z.boolean(),
    grossValueForAdvice: z.boolean(),
    expenses: z.enum(['statutory', 'flat']).nullable(),
    absenceFee: z.boolean(),
    absenceFeeHours: z.number().int().min(1).max(24).nullable(),
    paymentDay: DayOfMonth,
    acceptsPaper: z.boolean().nullable(),
    bookkeepingDeadlineDay: DayOfMonth,
    bookkeepingDeadlineDayExtended: DayOfMonth,
    payrollDeadlineDay: DayOfMonth,
    payrollCompletionDay: DayOfMonth,
    annualAccountsDeadline: AnnualAccountsDeadline.nullable(),
    probationClause: z.boolean(),
    excludedTaxAreas: z.array(ListItem).max(20),
    excludedLawAreas: z.array(ListItem).max(20),
    noticePeriod: NoticePeriod.nullable(),
    courtOfJurisdiction: z.boolean(),
    objectionAuthority: z.boolean(),
})
    .strict()
    .meta({ id: 'FirmStandards' });
/** The `firm_standard` blocks each value is printed by; the library's check holds them. */
export const firmStandardBlocks = {
    timeRounding: ['E2.ALLG.020', 'E2.ALLG.021'],
    mediumFeeMinimum: ['E2.ALLG.050'],
    grossValueForAdvice: ['E2.ALLG.060'],
    expenses: ['E2.ALLG.170', 'E2.ALLG.180'],
    absenceFee: ['E2.ALLG.200'],
    absenceFeeHours: ['E2.ALLG.200'],
    paymentDay: ['E2.ALLG.150'],
    acceptsPaper: ['E3.FIBU.010', 'E3.JA.010'],
    bookkeepingDeadlineDay: ['E3.FIBU.030'],
    bookkeepingDeadlineDayExtended: ['E3.FIBU.031'],
    payrollDeadlineDay: ['E3.LOHN.050'],
    payrollCompletionDay: ['E3.LOHN.060'],
    annualAccountsDeadline: ['E3.JA.070'],
    probationClause: ['E1.EINR.130'],
    excludedTaxAreas: ['E4.AUSS.010'],
    excludedLawAreas: ['E4.AUSS.020'],
    noticePeriod: ['E4.KUEND.030'],
    courtOfJurisdiction: ['E4.SCHL.030'],
    objectionAuthority: ['E4.RBEH.010'],
};
/** A position as the firm offers it: switched on or off, its price - `null` while none is set. */
export const CatalogPosition = z
    .object({ key: PositionKey, active: z.boolean(), price: Price.nullable() })
    .strict()
    .meta({ id: 'CatalogPosition' });
/** An hourly rate per qualification (E2.ALLG.010), e.g. „Steuerberater“ at 180 euros. */
export const HourlyRate = z
    .object({ label: z.string().trim().min(1).max(80), amount: Cents.positive() })
    .strict()
    .meta({ id: 'HourlyRate' });
const unique = (values) => new Set(values).size === values.length;
/** What a version of the firm's catalogue holds. */
export const CatalogContent = z
    .object({
    currency: z.literal('EUR'),
    areas: z
        .array(z.object({ area: CatalogArea, active: z.boolean() }).strict())
        .refine((areas) => areas.map((a) => a.area).join() === CatalogArea.options.join(), 'Every area once, in the order of CatalogArea'),
    bookkeeping: z
        .object({
        models: z.array(BookkeepingModel).refine(unique, 'Each model once'),
        packages: z.array(BookkeepingPackage).refine(unique, 'Each package once'),
    })
        .strict(),
    positions: z
        .array(CatalogPosition)
        .max(400)
        .refine((p) => unique(p.map((x) => x.key)), 'Each position once'),
    rates: z.array(HourlyRate).max(12),
    standards: FirmStandards,
    /**
     * Which questions of the contract draft the firm answers by its standard. Absent in versions
     * saved before there were any: then every question is asked (`draftQuestionsOf`).
     */
    draftQuestions: DraftQuestions.optional(),
})
    .strict()
    .meta({ id: 'CatalogContent' });
export const isAreaActive = (content, area) => content.areas.some((a) => a.area === area && a.active);
/** The services of the inquiry form the catalogue offers (ADR-071 §3), in the form's order. */
export const offeredInquiryServices = (content) => InquiryService.options.filter((s) => isAreaActive(content, areaOfInquiryService[s]));
const NO_STANDARDS = {
    timeRounding: null,
    mediumFeeMinimum: false,
    grossValueForAdvice: false,
    expenses: null,
    absenceFee: false,
    absenceFeeHours: null,
    paymentDay: null,
    acceptsPaper: null,
    bookkeepingDeadlineDay: null,
    bookkeepingDeadlineDayExtended: null,
    payrollDeadlineDay: null,
    payrollCompletionDay: null,
    annualAccountsDeadline: null,
    probationClause: false,
    excludedTaxAreas: [],
    excludedLawAreas: [],
    noticePeriod: null,
    courtOfJurisdiction: false,
    objectionAuthority: false,
};
/**
 * The standard catalogue before the firm saved one: every area, model, package and position the
 * library offers, switched on - and no price and no firm-standard value, since those are the
 * firm's to set.
 */
export const defaultCatalog = (template) => ({
    currency: 'EUR',
    areas: CatalogArea.options.map((area) => ({ area, active: true })),
    bookkeeping: { models: [...BookkeepingModel.options], packages: [...BookkeepingPackage.options] },
    positions: templatePositions(template).map((p) => ({ key: p.key, active: true, price: null })),
    rates: [],
    standards: NO_STANDARDS,
    draftQuestions: ASK_EVERYTHING,
});
/**
 * A saved catalogue on a newer library: the positions of the template in its order, a position
 * the library added switched off and without price, one it dropped left out - and every question
 * of the draft it does not answer asked.
 */
export function alignToTemplate(content, template) {
    const saved = new Map(content.positions.map((p) => [p.key, p]));
    return {
        ...content,
        draftQuestions: draftQuestionsOf(content.draftQuestions),
        positions: templatePositions(template).map((p) => saved.get(p.key) ?? { key: p.key, active: false, price: null }),
    };
}
//# sourceMappingURL=service-catalog.js.map